import { MutableRefObject, useEffect } from 'react'
import { ElementHash } from '../types'

const MAX_SCROLL_ATTEMPTS = 60 * 5 // 60 equals roughly 1 second if browser uses 60 FPS

interface ReturnType {
  isScrollTarget: boolean
}

/**
 * Hook that handles redirecting to a specific location hash and scrolling into view the component
 * that corresponds to that hash. Returns true if the ref references a component which should be scrolled to.
 *
 * requestAnimationFrame ensures that the scroll is run right before the next paint of the browser to provide an efficient animation.
 *
 * @param ref - React ref object for the component to scroll into view
 * @param locationHash - The location hash to scroll to
 * @param maxAttempts - The maximum number of attempts to scroll into view before giving up
 */
export const useScrollToHash = (
  ref: MutableRefObject<HTMLDivElement | null>,
  locationHash: ElementHash,
  offset?: number,
  maxAttempts = MAX_SCROLL_ATTEMPTS
): ReturnType => {
  const isScrollTarget = window.location.hash === locationHash
  const hasOffset = offset !== undefined && offset !== 0

  useEffect(() => {
    if (isScrollTarget) {
      let attemptCounter = 0

      const scrollToElement = (): void => {
        if (ref.current) {
          const elementPosition = ref.current.getBoundingClientRect().top
          const elementTop = hasOffset ? elementPosition - offset : elementPosition
          window.scrollTo({ top: elementTop, behavior: 'smooth' })
        } else if (attemptCounter < maxAttempts) {
          attemptCounter++
          requestAnimationFrame(scrollToElement)
        }
      }

      requestAnimationFrame(scrollToElement)
    }
  }, [isScrollTarget, ref, maxAttempts, locationHash, hasOffset, offset])

  return { isScrollTarget }
}
